package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.Checkbox
import com.varabyte.kobweb.silk.components.forms.CheckboxSize
import com.varabyte.kobweb.silk.components.forms.CheckedState
import com.varabyte.kobweb.silk.components.forms.TriCheckbox
import com.varabyte.kobweb.silk.components.forms.Input
import com.varabyte.kobweb.silk.components.forms.InputSize
import com.varabyte.kobweb.silk.components.icons.SearchIcon
import com.varabyte.kobweb.silk.components.overlay.AdvancedPopover
import com.varabyte.kobweb.silk.components.overlay.KeepPopupOpenStrategy
import com.varabyte.kobweb.silk.components.overlay.OpenClosePopupStrategy
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.PopupPlacementStrategy
import com.varabyte.kobweb.silk.components.overlay.manual
import com.varabyte.kobweb.silk.components.overlay.never
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.Node as DomNode
import org.w3c.dom.events.EventListener

/*
 * Per-column filter control: a magnifier at the right edge of the header that opens a popover with
 * a text input and, for categorical columns, the list of distinct values.
 *
 * Both the magnifier and the sort control are real `<button>` siblings inside a non-interactive
 * `<th>`, so no button is nested in another interactive element. Inside the popover, the clear-text
 * brush is likewise a sibling of the search input.
 */

val ColumnHeaderStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .gap(0.25.cssRem)
        .width(100.percent)
        .styleModifier { property("justify-content", "space-between") }
}

/** Label, sort chevron and priority ordinal, kept together on the left. */
val ColumnHeaderLabelStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .gap(0.25.cssRem)
}

/** `(N)` plus the magnifier, kept together on the right. */
val ColumnFilterGroupStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .gap(0.25.cssRem)
}

val ColumnFilterTriggerStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .padding(0.cssRem)
        .margin(0.cssRem)
        .backgroundColor(Colors.Transparent)
        .border(0.cssRem)
        .color(IndustrialPalette.Muted)
        .cursor(Cursor.Pointer)
        // Escape hatch: no composed helper for line-height, and the icon must not grow the header.
        .styleModifier { property("line-height", "1") }
}

val ColumnFilterCountStyle = CssStyle.base {
    Modifier
        .fontSize(0.6875.cssRem)
        .color(IndustrialPalette.Muted)
        .styleModifier { property("font-variant-numeric", "tabular-nums") }
}

val ColumnFilterPopupStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.25.cssRem)
        .padding(0.375.cssRem)
        .minWidth(11.cssRem)
        // The popover floats over the table; the Silk surface has `z-index: auto`, which loses to
        // the table's own stacking context.
        .styleModifier { property("z-index", "1000") }
        .boxShadow(
            offsetY = 0.125.cssRem,
            blurRadius = 0.5.cssRem,
            color = IndustrialPalette.Ink.copyf(alpha = 0.15f),
        )
}

val ColumnFilterActionsStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .width(100.percent)
        .styleModifier { property("justify-content", "flex-end") }
}

val ColumnFilterInputStyle = CssStyle {
    base {
        Modifier
            .color(IndustrialPalette.Ink)
            .fontSize(0.8125.cssRem)
            .backgroundColor(IndustrialPalette.White)
            .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
            .borderRadius(0.25.cssRem)
            .padding(0.375.cssRem, 0.625.cssRem)
    }
    // The brush beside the input is the single clear action; hide Chromium's own search "X".
    cssRule("::-webkit-search-cancel-button") {
        Modifier.display(DisplayStyle.None)
    }
}

/** Search input and its clear button side by side: neither wraps the other. */
val ColumnFilterSearchRowStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .gap(0.25.cssRem)
        .width(100.percent)
}

val ColumnFilterClearButtonStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .alignItems(AlignItems.Center)
        .padding(0.125.cssRem)
        .margin(0.cssRem)
        .backgroundColor(Colors.Transparent)
        .border(0.cssRem)
        .color(IndustrialPalette.Muted)
        .cursor(Cursor.Pointer)
        // Escape hatch: no composed helper for line-height, and the icon must not grow the row.
        .styleModifier { property("line-height", "1") }
}

val SelectAllRowStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .padding(bottom = 0.25.cssRem)
        // 1px hairline separator stays in px per the units policy.
        .borderBottom(1.px, LineStyle.Solid, IndustrialPalette.Grid)
}

val ColumnFilterHintStyle = CssStyle.base {
    Modifier
        .fontSize(0.6875.cssRem)
        .color(IndustrialPalette.Muted)
}

val FilterOptionsStyle = CssStyle.base {
    Modifier
        // The options column is a flex item of the inner Column (align-items: start by default), so
        // without an explicit width it shrinks to the longest label and the scrollbar floats mid-popup.
        .width(100.percent)
        .display(DisplayStyle.Flex)
        .flexDirection(FlexDirection.Column)
        .gap(0.25.cssRem)
        .maxHeight(14.cssRem)
        .overflow(Overflow.Hidden, Overflow.Auto)
}

val FilterEmptyStyle = CssStyle.base {
    Modifier
        .fontSize(0.8125.cssRem)
        .color(IndustrialPalette.Muted)
}

/**
 * Line between the values the filter pins and the rest of the list. The options column is a flex
 * column with a `gap`, so the line only needs its own 1px height; `flex-shrink: 0` keeps it from
 * collapsing when the list overflows.
 */
val FilterOptionsSeparatorStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(1.px)
        .backgroundColor(IndustrialPalette.Grid)
        .styleModifier { property("flex-shrink", "0") }
}

@Composable
internal fun ColumnFilterButton(
    filterId: String,
    label: String,
    filter: ColumnFilter,
    options: List<String>?,
    onFilterChange: (ColumnFilter) -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    // Value whose checkbox must get the focus back after the live re-group moves its row.
    var pendingFocus by remember { mutableStateOf<String?>(null) }
    val openCloseStrategy = remember { OpenClosePopupStrategy.manual() }
    // `never()` — NOT `manual()`, whose `shouldKeepOpen` defaults to true and vetoes every hide request.
    val keepOpenStrategy = remember { KeepPopupOpenStrategy.never() }
    val placementStrategy = remember { PopupPlacementStrategy.of(PopupPlacement.BottomLeft, offsetPixels = 4) }
    val popupElement = remember { ElementHolder<HTMLElement>() }
    val inputElement = remember { ElementHolder<HTMLInputElement>() }

    fun setOpen(value: Boolean) {
        if (!value) pendingFocus = null
        open = value
        openCloseStrategy.isOpen = value
    }

    /** Moves a row between the pinned and unpinned groups, keeping keyboard focus on the row. */
    fun toggleOption(option: String) {
        val focusedOption = document.activeElement
            ?.closest("label[data-filter-option]")
            ?.getAttribute("data-filter-option")
        if (focusedOption == option) pendingFocus = option
        onFilterChange(filter.copy(selection = filter.selection.toggled(option)))
    }

    /** Finds the moved row by its data attribute (no selector escaping for arbitrary values). */
    fun focusOption(option: String) {
        val popup = popupElement.element ?: return
        val labels = popup.querySelectorAll("label[data-filter-option]")
        for (index in 0 until labels.length) {
            val label = labels.item(index) as? Element ?: continue
            if (label.getAttribute("data-filter-option") == option) {
                (label.querySelector("input") as? HTMLElement)?.focus()
                return
            }
        }
    }

    // Escape hatch: no Silk/Compose primitive covers outside clicks or Escape; the listeners are
    // removed on dispose. The trigger is recognised by its data attribute so it needs no ref.
    DisposableEffect(Unit) {
        val onMouseDown = EventListener { event ->
            val target = event.target as? DomNode
            val insideTrigger = (target as? Element)
                ?.closest("[data-column-filter=\"$filterId\"]") != null
            val insidePopup = target != null && popupElement.element?.contains(target) == true
            if (!insideTrigger && !insidePopup) setOpen(false)
        }
        val onKeyDown = EventListener { event ->
            if (open && (event as? KeyboardEvent)?.key == "Escape") {
                setOpen(false)
                (document.querySelector("[data-column-filter=\"$filterId\"]") as? HTMLElement)?.focus()
            }
        }
        document.addEventListener("mousedown", onMouseDown)
        window.addEventListener("keydown", onKeyDown)
        onDispose {
            document.removeEventListener("mousedown", onMouseDown)
            window.removeEventListener("keydown", onKeyDown)
        }
    }

    val count = filter.activeCount
    val ariaLabel = buildString {
        append("Filtrar $label")
        if (count > 0) append(", $count ${if (count == 1) "filtro ativo" else "filtros ativos"}")
    }
    val canClearText = filter.text.isNotEmpty()

    HtmlButton(
        attrs = ColumnFilterTriggerStyle.toModifier()
            .then(Modifier.color(if (filter.isActive) IndustrialPalette.Steel else IndustrialPalette.Muted))
            .toAttrs {
                attr("type", "button")
                attr("data-column-filter", filterId)
                attr("aria-label", ariaLabel)
                attr("aria-expanded", open.toString())
                attr("aria-controls", "colfilter-$filterId")
                onClick { setOpen(!open) }
            },
    ) {
        SearchIcon(Modifier.size(0.875.cssRem))
    }

    AdvancedPopover(
        target = ElementTarget.PreviousSibling,
        modifier = ColumnFilterPopupStyle.toModifier(),
        openCloseStrategy = openCloseStrategy,
        keepOpenStrategy = keepOpenStrategy,
        placementStrategy = placementStrategy,
        ref = ref { element -> popupElement.element = element },
    ) {
        Column(Modifier.gap(0.25.cssRem).width(100.percent)) {
            if (filter.isActive) {
                Row(modifier = ColumnFilterActionsStyle.toModifier()) {
                    Button(
                        onClick = {
                            pendingFocus = null
                            onFilterChange(ColumnFilter.Empty)
                        },
                        variant = LinkButtonVariant,
                        size = ButtonSize.SM,
                    ) {
                        Text("Limpar")
                    }
                }
            }

            Row(modifier = ColumnFilterSearchRowStyle.toModifier()) {
                Input(
                    type = InputType.Search,
                    value = filter.text,
                    onValueChange = { onFilterChange(filter.copy(text = it)) },
                    modifier = ColumnFilterInputStyle.toModifier()
                        // flex: 1 1 0 — fills the row without letting the browser's default
                        // input width push the brush out of the popover.
                        .then(
                            Modifier.styleModifier {
                                property("flex", "1 1 0")
                                property("min-width", "0")
                            },
                        )
                        .then(Modifier.id("colfilter-$filterId-search")),
                    placeholder = "Buscar…",
                    size = InputSize.SM,
                    ref = ref { element -> inputElement.element = element },
                )
                // Standalone button next to the input: neither element nests the other.
                HtmlButton(
                    attrs = ColumnFilterClearButtonStyle.toModifier()
                        .then(
                            Modifier.color(
                                if (canClearText) IndustrialPalette.Muted else IndustrialPalette.Border,
                            ),
                        )
                        .then(
                            Modifier.cursor(
                                if (canClearText) Cursor.Pointer else Cursor.Default,
                            ),
                        )
                        .toAttrs {
                            attr("type", "button")
                            attr("data-column-filter-clear", filterId)
                            attr("aria-label", "Limpar texto do filtro de $label")
                            attr("title", "Limpar texto")
                            if (!canClearText) attr("disabled", "")
                            onClick {
                                // Keep the caret in the input after the button disables itself.
                                inputElement.element?.focus()
                                onFilterChange(filter.copy(text = ""))
                            }
                        },
                ) {
                    BrushIcon(Modifier.size(0.875.cssRem))
                }
            }

            if (options != null) {
                val merged = remember(options, filter.selection) {
                    (options + filter.selection.values).distinct().sorted()
                }
                val visible = remember(merged, filter.query) { merged.filter { matchesContains(it, filter.query) } }
                // "Pinned" = the values this column's filter keeps: the checked ones in "only
                // these" mode and the unchecked (excluded) ones in "all except" mode. They come
                // first so the applied filter is easy to review and undo. The list is re-partitioned
                // on every toggle; `pendingFocus` restores the keyboard focus to the moved row.
                val (pinned, rest) = remember(visible, filter.selection) {
                    visible.partition { it in filter.selection.values }
                }

                SelectAllRow(label = label, state = filter.selection.selectAllState) {
                    pendingFocus = null
                    onFilterChange(filter.copy(selection = filter.selection.toggledSelectAll()))
                }

                if (visible.isEmpty()) {
                    Div(attrs = FilterEmptyStyle.toModifier().toAttrs()) { Text("Sem opções.") }
                } else {
                    Div(
                        attrs = FilterOptionsStyle.toModifier()
                            .then(Modifier.id("colfilter-$filterId-options"))
                            .toAttrs(),
                    ) {
                        for (option in pinned) {
                            FilterOptionRow(option, checked = filter.selection.isChecked(option)) {
                                toggleOption(option)
                            }
                        }
                        // The line only makes sense when both groups are visible: an untouched
                        // column (All) or a fully selected one (AllExcept with no exclusions) has
                        // nothing to separate.
                        if (pinned.isNotEmpty() && rest.isNotEmpty()) {
                            Div(
                                attrs = FilterOptionsSeparatorStyle.toModifier().toAttrs {
                                    attr("role", "separator")
                                    attr("aria-orientation", "horizontal")
                                    attr("data-filter-selected-divider", "true")
                                },
                            )
                        }
                        for (option in rest) {
                            FilterOptionRow(option, checked = filter.selection.isChecked(option)) {
                                toggleOption(option)
                            }
                        }
                    }
                }

                // Values hidden by the query keep counting: say so, otherwise "(N)" would disagree
                // with the visible list.
                val hidden = filter.selection.values.count { !matchesContains(it, filter.query) }
                if (hidden > 0) {
                    val text = when (filter.selection) {
                        is FilterSelection.AllExcept -> "$hidden desmarcado(s) fora da busca — ainda excluído(s)"
                        else -> "$hidden selecionado(s) fora da busca — ainda ativo(s)"
                    }
                    Div(attrs = ColumnFilterHintStyle.toModifier().toAttrs()) { Text(text) }
                }
            }
        }
    }

    // The row was re-partitioned into the other group; wait for the DOM to apply the new order and
    // only then move the focus, otherwise the browser drops it on <body>.
    LaunchedEffect(filter.selection, pendingFocus) {
        val option = pendingFocus ?: return@LaunchedEffect
        withFrameNanos { }
        focusOption(option)
        pendingFocus = null
    }

    LaunchedEffect(open) {
        if (open) inputElement.element?.focus()
    }
}

/** One categorical value: the checkbox and its label, with a stable hook for the UI tests. */
@Composable
private fun FilterOptionRow(option: String, checked: Boolean, onToggle: () -> Unit) {
    Checkbox(
        checked = checked,
        onCheckedChange = { onToggle() },
        modifier = Modifier.width(100.percent)
            .then(
                Modifier.attrsModifier {
                    attr("data-filter-option", option)
                },
            ),
        size = CheckboxSize.SM,
        content = { Text(option) },
    )
}

/**
 * "Selecionar tudo" acima da lista. O modelo decide o próximo estado (o `CheckedState` sugerido pelo
 * Silk é ignorado), e o `ref` mantém o `indeterminate`/`aria-checked` do `<input>` em sincronia —
 * o Silk desenha o quadrado pelo `CheckedState`, mas não atualiza essas duas propriedades do DOM.
 */
@Composable
private fun SelectAllRow(label: String, state: SelectAllState, onToggle: () -> Unit) {
    val checkedState = when (state) {
        SelectAllState.CHECKED -> CheckedState.Checked
        SelectAllState.INDETERMINATE -> CheckedState.Indeterminate
        SelectAllState.UNCHECKED -> CheckedState.Unchecked
    }

    TriCheckbox(
        checked = checkedState,
        onCheckedChange = { onToggle() },
        modifier = SelectAllRowStyle.toModifier().then(
            Modifier.attrsModifier {
                attr("data-filter-all", "true")
                attr("data-select-all-state", state.name.lowercase())
            },
        ),
        size = CheckboxSize.SM,
        ref = ref(state) { element ->
            val input = element.querySelector("input") as? HTMLInputElement ?: return@ref
            input.indeterminate = state == SelectAllState.INDETERMINATE
            input.checked = state != SelectAllState.UNCHECKED
            input.setAttribute(
                "aria-checked",
                when (state) {
                    SelectAllState.CHECKED -> "true"
                    SelectAllState.INDETERMINATE -> "mixed"
                    SelectAllState.UNCHECKED -> "false"
                },
            )
            input.setAttribute("aria-label", "Selecionar tudo em $label")
        },
        content = { Text("Selecionar tudo") },
    )
}
